import { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const LABEL_OPTIONS = ['Processing Fee', 'Deposit', 'Balance on Arrival', 'Other'];
const METHOD_OPTIONS = ['Bank Transfer', 'Mobile Money', 'Cash'];

export default function PaymentUploadModal({ applicationId, onClose, onSuccess }) {
  const [label, setLabel] = useState('Processing Fee');
  const [customLabel, setCustomLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [method, setMethod] = useState('Bank Transfer');
  const [proof, setProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalLabel = label === 'Other' ? customLabel.trim() : label;
    if (!finalLabel || !amount || !proof) {
      setError('Please fill in the fee type, amount, and attach your proof of payment.');
      return;
    }
    setSubmitting(true);
    setError('');
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('label', finalLabel);
    fd.append('amount', amount);
    fd.append('currency', currency);
    fd.append('method', method);
    fd.append('proof', proof);

    try {
      const res = await fetch(`${API_BASE}/portal/applications/${applicationId}/payments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Something went wrong');
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[100] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl space-y-5 my-12"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-xl font-bold text-[#00255a]">Upload Proof of Payment</h3>
          <p className="text-sm text-[#64748b] mt-1">Your agent will confirm this once reviewed.</p>
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>}

        <div>
          <label className="block text-xs font-bold text-[#44474f] uppercase mb-1">What is this payment for?</label>
          <select
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
          >
            {LABEL_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          {label === 'Other' && (
            <input
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="Describe this payment"
              className="w-full mt-2 px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-[#44474f] uppercase mb-1">Amount</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#44474f] uppercase mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
            >
              <option>USD</option> {/* US Dollar */}
              <option>EUR</option> {/* Euro */}
              <option>GBP</option> {/* British Pound */}
              <option>TRY</option> {/* Turkish Lira */}
              <option>CHF</option> {/* Swiss Franc */}
              <option>SEK</option> {/* Swedish Krona */}
              <option>NOK</option> {/* Norwegian Krone */}
              <option>DKK</option> {/* Danish Krone */}
              <option>PLN</option> {/* Polish Zloty */}
              <option>CZK</option> {/* Czech Koruna */}
              <option>HUF</option> {/* Hungarian Forint */}
              <option>RON</option> {/* Romanian Leu */}
              <option>BGN</option> {/* Bulgarian Lev */}
              <option>ISK</option> {/* Icelandic Krona */}

            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-[#44474f] uppercase mb-1">Payment Method</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
          >
            {METHOD_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-[#44474f] uppercase mb-1">Proof of Payment</label>
          <input
            type="file"
            required
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={(e) => setProof(e.target.files[0])}
            className="w-full text-sm"
          />
          <p className="text-xs text-[#64748b] mt-1">Screenshot or scan of your receipt/transfer confirmation.</p>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            {submitting ? 'Submitting...' : 'Submit Payment'}
          </button>
        </div>
      </form>
    </div>
  );
}